import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Clock, CheckCircle2, Play, Trash2, Edit3, Eye, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DocumentUploader } from '@/components/documents/DocumentUploader';
import { PromptEditor } from '@/components/projects/PromptEditor';
import { ChatSidebar } from '@/components/chat/ChatSidebar';
import DORAProjectView from '@/components/dora/DORAProjectView';
import { ISOProjectView } from '@/components/iso/ISOProjectView';
import { SOC2ProjectView } from '@/components/soc2/SOC2ProjectView';
import { ESGProjectView } from '@/components/esg/ESGProjectView';
import { QuestionnaireProjectView } from '@/components/questionnaire/QuestionnaireProjectView';
import { RFPProjectView } from '@/components/rfp/RFPProjectView';
import { ProposalWizard } from '@/components/proposals/ProposalWizard';
import { useStore } from '@/store/useStore';
import { projectsApi, documentsApi, processingApi, masterReportsApi, handleApiError } from '@/services/api';
import { toast } from 'sonner';
import type { Project, Document } from '@/types';

export default function ProjectView() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { setCurrentProject } = useStore();
  
  const [project, setProject] = useState<Project | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const [docAnnotation, setDocAnnotation] = useState<any>(null);
  const [processingStatus, setProcessingStatus] = useState<any>(null);
  const [showCaseOverview, setShowCaseOverview] = useState(false);
  const [chatWidth, setChatWidth] = useState(600);
  const [isResizing, setIsResizing] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  
  // Use global model selection from store
  const { selectedModel } = useStore();
  const [showSimpleTimeline, setShowSimpleTimeline] = useState(false);
  const [caseOverview, setCaseOverview] = useState<string | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const wasProcessingRef = useRef(false);

  useEffect(() => {
    if (projectId) {
      loadProject();
      loadDocuments();
      // Don't auto-load case overview - let user click button to avoid rate limits
      // loadCachedCaseOverview();
    }
  }, [projectId]);

  // Handle chat resize
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      
      const newWidth = window.innerWidth - e.clientX;
      // Constrain between 400px and 80% of screen
      const minWidth = 400;
      const maxWidth = window.innerWidth * 0.8;
      setChatWidth(Math.min(Math.max(newWidth, minWidth), maxWidth));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const loadCachedCaseOverview = async () => {
    try {
      const result = await processingApi.getCaseOverview(Number(projectId), selectedModel);
      if (result.overview) {
        setCaseOverview(result.overview);
      }
    } catch (error: any) {
      // Stop retrying on rate limit errors
      if (error?.response?.status === 500 && error?.response?.data?.detail?.includes('rate_limit')) {
        console.warn('Case overview rate limited - stop retrying');
        return;
      }
      // Silently fail for other errors
      console.log('No cached case overview available yet');
    }
  };

  // Persist model selection to localStorage
  useEffect(() => {
    localStorage.setItem('selectedLLMModel', selectedModel);
  }, [selectedModel]);

  const loadProject = async (): Promise<Project | null> => {
    try {
      const data = await projectsApi.get(Number(projectId));
      setProject(data);
      setCurrentProject(data);
      return data;
    } catch (error) {
      toast.error(handleApiError(error));
      navigate('/');
      return null;
    }
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const data = await documentsApi.list(Number(projectId));
      setDocuments(data);
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleProcessAll = async (forceReprocess: boolean = false) => {
    if (forceReprocess && !window.confirm('Reprocess all documents? This will replace existing extracted data.')) {
      return;
    }

    try {
      setProcessing(true);
      
      // Show immediate processing status
      setProcessingStatus({
        is_processing: true,
        processed: 0,
        total: documents.length,
        current_document: 'Starting...',
        eta_seconds: 0,
        memory_used_gb: 0,
        memory_free_gb: 0
      });
      
      const result = await processingApi.processProject(Number(projectId), false, forceReprocess, selectedModel);
      console.log('[REPROCESS] API call successful, result:', result);
      toast.success(`Processing ${result.document_count} documents! Estimated time: ${result.estimated_minutes} minutes.`);
      
      // Immediately refresh to show pending statuses
      console.log('[REPROCESS] Refreshing documents and project...');
      await loadDocuments();
      await loadProject();
      
      // Track that processing has started
      wasProcessingRef.current = true;

      // Poll for updates every 5 seconds with ETA tracking
      const interval = setInterval(async () => {
        // Fetch processing status for ETA
        try {
          const status = await processingApi.getProcessingStatus(Number(projectId));
          // Only update if we got valid status data - keeps banner visible
          if (status && (status.is_processing !== undefined || status.total_docs > 0)) {
            setProcessingStatus(status);
            console.log('[REPROCESS] Processing status:', status);
          }
        } catch (error) {
          console.error('[REPROCESS] Failed to fetch processing status:', error);
        }
        
        const updatedProject = await loadProject();
        await loadDocuments();
        
        // Stop polling when all documents are processed
        if (updatedProject && updatedProject.document_count === updatedProject.processed_document_count) {
          console.log('[REPROCESS] All documents processed, stopping polling');
          setProcessingStatus(null);
          setProcessing(false);
          clearInterval(interval);

          if (wasProcessingRef.current) {
            wasProcessingRef.current = false;
            toast.success(
              `Processing complete! ${updatedProject.processed_document_count}/${updatedProject.document_count} documents processed.`
            );
            try {
              const audio = new Audio('/notification.mp3');
              audio.volume = 0.3;
              audio.play().catch(() => {});
            } catch {}
          }
          
          // Load the auto-generated case overview
          setTimeout(() => loadCachedCaseOverview(), 2000);
        }
      }, 5000);
      
      // Stop polling after 10 minutes as fallback
      setTimeout(() => {
        console.log('[REPROCESS] Timeout reached, stopping polling');
        setProcessingStatus(null);
        setProcessing(false);
        wasProcessingRef.current = false;
        clearInterval(interval);
      }, 600000);
    } catch (error) {
      console.error('[REPROCESS] Error:', error);
      toast.error(handleApiError(error));
    } finally {
      setProcessing(false);
    }
  };

  const handleViewDocument = async (doc: Document) => {
    try {
      setSelectedDoc(doc);
      // Fetch annotation data
      const response = await fetch(`/api/v1/documents/${doc.id}/annotations-v2`);
      if (response.ok) {
        const data = await response.json();
        setDocAnnotation(data);
      } else {
        setDocAnnotation(null);
      }
    } catch (error) {
      console.error('Failed to load annotation:', error);
      setDocAnnotation(null);
    }
  };

  const handleDeleteDocument = async (docId: number) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await documentsApi.delete(docId);
      toast.success('Document deleted');
      loadDocuments();
      loadProject();
    } catch (error) {
      toast.error(handleApiError(error));
    }
  };

  const handleDeleteProject = async () => {
    if (!window.confirm(`Are you sure you want to delete "${project?.name}"? This will delete all documents and cannot be undone.`)) {
      return;
    }

    try {
      await projectsApi.delete(Number(projectId));
      toast.success('Project deleted');
      navigate('/dashboard');
    } catch (error) {
      toast.error(handleApiError(error));
    }
  };

  const handleGenerateCaseOverview = async () => {
    // If we already have a cached overview, show it immediately
    if (caseOverview) {
      setShowCaseOverview(true);
      return;
    }
    
    // Otherwise, fetch/generate
    try {
      setLoadingOverview(true);
      setShowCaseOverview(true);
      
      const result = await processingApi.getCaseOverview(Number(projectId), selectedModel);
      setCaseOverview(result.overview);
      
      if (result.cached) {
        toast.success('Case overview loaded from cache');
      } else if (result.regenerated) {
        toast.success('Case overview regenerated with new document data');
      } else {
        const estimatedTime = result.estimated_seconds || 45;
        toast.success(`Case overview generated successfully (took ~${estimatedTime}s)`);
      }
    } catch (error) {
      toast.error(handleApiError(error));
      setShowCaseOverview(false);
    } finally {
      setLoadingOverview(false);
    }
  };

  const handleExportMasterReport = async () => {
    try {
      setGeneratingReport(true);
      toast.info('Generating master reports... This may take a moment.');
      
      const response = await masterReportsApi.export(Number(projectId));
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `MasterReports_${project?.name || projectId}.zip`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match?.[1]) filename = match[1];
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Master reports downloaded successfully!');
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setGeneratingReport(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100';
      case 'processing':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-4 w-4" />;
      case 'processing':
        return <Clock className="h-4 w-4 animate-spin" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const pendingCount = documents.filter(d => d.processing_status === 'pending').length;
  const completedCount = documents.filter(d => d.processing_status === 'completed').length;

  // Handle chat resize - MUST be before any conditional returns
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isResizing) {
        const newWidth = window.innerWidth - e.clientX;
        if (newWidth >= 400 && newWidth <= 1000) {
          setChatWidth(newWidth);
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  if (!project) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Route DORA compliance projects to specialized view
  if (project.use_case === 'compliance_dora') {
    return <DORAProjectView />;
  }

  // Route ISO 27001 projects to specialized dashboard
  if (project.use_case === 'compliance_iso_27001') {
    return <ISOProjectView />;
  }

  // Route Security Questionnaire projects to specialized view
  if (project.use_case === 'security_questionnaire') {
    return <QuestionnaireProjectView />;
  }

  // Route ESG/CSRD projects to specialized dashboard
  if (project.use_case === 'compliance_esg') {
    return <ESGProjectView />;
  }

  // Route SOC 2 projects to specialized dashboard
  if (project.use_case === 'compliance_soc2') {
    return <SOC2ProjectView />;
  }

  // Route RFP projects to specialized view
  if (project.use_case === 'rfp_response') {
    return <RFPProjectView />;
  }

  // Route Service Proposal projects to proposal wizard
  if (project.use_case === 'service_proposal') {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Dashboard
        </Button>
        <ProposalWizard projectId={project.id} projectName={project.name} />
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
        {/* Main Content */}
        <div className="flex-1 overflow-y-auto" style={{ marginRight: `${chatWidth}px` }}>
          <div className="space-y-6 p-6">
            {/* Back Button */}
            <div>
              <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
                <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Projects
        </Button>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">{project.name}</h1>
            {project.description && (
              <p className="text-muted-foreground">{project.description}</p>
            )}
          </div>
          
          <div className="flex flex-col gap-2 items-end">
            <div className="flex gap-2 items-center">
              <Button variant="outline" size="sm" onClick={handleGenerateCaseOverview}>
                📊 Detailed Case Overview
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowSimpleTimeline(true)}>
                📅 Timeline
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportMasterReport}
                disabled={generatingReport || completedCount === 0}
              >
                {generatingReport ? (
                  <>
                    <Clock className="h-4 w-4 mr-1 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-1" />
                    Master Report
                  </>
                )}
              </Button>
            </div>
            <div className="flex gap-2 items-center">
              <Button variant="outline" onClick={() => setShowPromptEditor(true)}>
                <Edit3 className="h-4 w-4 mr-2" />
                Edit Extraction Prompt
              </Button>
              {pendingCount > 0 && (
                <Button onClick={() => handleProcessAll(false)} disabled={processing} variant="default">
                  <Play className="h-4 w-4 mr-2" />
                  Process Pending ({pendingCount})
                </Button>
              )}
              <Button onClick={() => handleProcessAll(true)} disabled={processing} variant="secondary">
                <Play className="h-4 w-4 mr-2" />
                Reprocess All
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Processing Status Banner with ETA */}
      {processingStatus && processingStatus.is_processing && (
        <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                <div>
                  <div className="font-semibold text-blue-900 dark:text-blue-100">
                    ✅ Processing Active
                  </div>
                  <div className="text-sm text-blue-700 dark:text-blue-300">
                    {processingStatus.current_doc ? (
                      <span>📄 {processingStatus.current_doc}</span>
                    ) : (
                      <span>⏳ Starting next document...</span>
                    )}
                  </div>
                  {/* Last updated timestamp */}
                  <div className="text-xs text-blue-500 dark:text-blue-400 mt-1">
                    Updated {new Date().toLocaleTimeString()}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                  {processingStatus.completed_docs} / {processingStatus.total_docs}
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-300">
                  ETA: {processingStatus.eta_formatted}
                </div>
                <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                  ~{Math.round(processingStatus.avg_time_per_doc)}s per document
                </div>
                {processingStatus.memory_used_gb !== undefined && processingStatus.memory_total_gb && (
                  <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    💾 {processingStatus.memory_used_gb}GB / {processingStatus.memory_total_gb}GB ({Math.round((processingStatus.memory_used_gb / processingStatus.memory_total_gb) * 100)}% used)
                  </div>
                )}
              </div>
            </div>
            {/* Warnings for timeouts and failures */}
            {(processingStatus.timeout_count > 0 || processingStatus.failed_count > 0) && (
              <div className="mt-3 flex gap-3 text-xs">
                {processingStatus.timeout_count > 0 && (
                  <div className="flex items-center gap-1 text-amber-700 dark:text-amber-400">
                    <span>⏱️</span>
                    <span>{processingStatus.timeout_count} chunk timeouts (processing continues)</span>
                  </div>
                )}
                {processingStatus.failed_count > 0 && (
                  <div className="flex items-center gap-1 text-red-700 dark:text-red-400">
                    <span>❌</span>
                    <span>{processingStatus.failed_count} documents failed</span>
                  </div>
                )}
              </div>
            )}
            {/* Progress bar */}
            <div className="mt-4 w-full bg-blue-200 dark:bg-blue-900 rounded-full h-2">
              <div 
                className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full transition-all duration-500"
                style={{ width: `${(processingStatus.completed_docs / processingStatus.total_docs) * 100}%` }}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{documents.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Processed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600 dark:text-green-400">
              {completedCount}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pending
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">
              {pendingCount}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tab Navigation */}
      <>
          {/* Upload Section - Collapsible */}
          <Card>
            <CardHeader className="cursor-pointer" onClick={() => setShowUploader(!showUploader)}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Upload Documents</CardTitle>
                <Button variant="ghost" size="sm">
                  {showUploader ? '▼' : '▶'}
                </Button>
              </div>
            </CardHeader>
            {showUploader && (
              <CardContent>
                <DocumentUploader
                  projectId={Number(projectId)}
                  onUploadComplete={() => {
                    loadDocuments();
                    loadProject();
                  }}
                />
              </CardContent>
            )}
          </Card>

          {/* Documents List */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Documents</h2>
        
            {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : documents.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No documents yet</h3>
              <p className="text-muted-foreground text-center max-w-md">
                Upload your first document to start analyzing
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {documents.map((doc) => (
              <Card key={doc.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {getStatusIcon(doc.processing_status)}
                        <CardTitle className="text-base">{doc.filename}</CardTitle>
                      </div>
                      {doc.latest_summary && (
                        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                          {doc.latest_summary}
                        </p>
                      )}
                      <CardDescription className="flex items-center gap-4 text-xs">
                        <span>{(doc.file_size / 1024).toFixed(1)} KB</span>
                        <span>{doc.file_type.toUpperCase()}</span>
                        {doc.annotation_count !== undefined && doc.annotation_count > 0 && (
                          <span className="text-green-600 dark:text-green-400">
                            {doc.annotation_count} annotations
                          </span>
                        )}
                      </CardDescription>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge className={getStatusColor(doc.processing_status)}>
                        {doc.processing_status}
                      </Badge>
                      
                      {doc.processing_status === 'completed' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDocument(doc)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteDocument(doc.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {doc.error_message && (
                    <div className="mt-2 p-2 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
                      {doc.error_message}
                    </div>
                  )}
                </CardHeader>
              </Card>
            ))}
          </div>
        )}
          </div>
        </>

      {/* Prompt Editor Dialog */}
      {showPromptEditor && (
        <PromptEditor
          projectId={Number(projectId)}
          open={showPromptEditor}
          onClose={() => setShowPromptEditor(false)}
        />
      )}

      {/* Document Detail Dialog */}
      <Dialog open={selectedDoc !== null} onOpenChange={() => setSelectedDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedDoc?.filename}</DialogTitle>
          </DialogHeader>
          
          {docAnnotation ? (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between pb-2 border-b">
                <div className="flex items-center gap-2">
                  {docAnnotation.data.doc_type && (
                    <Badge className="bg-blue-600 text-white">
                      {docAnnotation.data.doc_type.toUpperCase()}
                    </Badge>
                  )}
                </div>
                <Badge variant="secondary">
                  Confidence: {Math.round((docAnnotation.confidence || 0) * 100)}%
                </Badge>
              </div>

              {/* Filed By */}
              {docAnnotation.data.filed_by && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-600 mb-1">Filed By</h4>
                  <p className="text-sm text-gray-700">{docAnnotation.data.filed_by}</p>
                </div>
              )}

              {/* Filed Date */}
              {docAnnotation.data.filed_date && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-600 mb-1">Filed Date</h4>
                  <p className="text-sm text-gray-700">{docAnnotation.data.filed_date}</p>
                </div>
              )}

              {/* Summary */}
              {docAnnotation.data.summary && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-600 mb-1">Summary</h4>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {docAnnotation.data.summary}
                  </div>
                </div>
              )}

              {/* Key Dates */}
              {docAnnotation.data.key_dates && docAnnotation.data.key_dates.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-600 mb-2">Key Dates</h4>
                  <div className="space-y-2">
                    {docAnnotation.data.key_dates.map((dateItem: any, idx: number) => (
                      <div key={idx} className="flex items-start gap-3 text-sm">
                        <span className="font-medium text-blue-600 whitespace-nowrap">
                          {dateItem.date}
                        </span>
                        <span className="text-gray-700">{dateItem.event}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No annotation data available</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Prompt Editor Dialog */}
      {showPromptEditor && (
        <PromptEditor
          projectId={Number(projectId)}
          open={showPromptEditor}
          onClose={() => setShowPromptEditor(false)}
        />
      )}

      {/* Detailed Case Overview Dialog */}
      <Dialog open={showCaseOverview} onOpenChange={setShowCaseOverview}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>📋 Detailed Case Overview</DialogTitle>
            <p className="text-sm text-muted-foreground">
              AI-synthesized overview from {documents.filter(d => d.processing_status === 'completed').length} processed documents
            </p>
          </DialogHeader>
          <div className="mt-4">
            {loadingOverview ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" />
                <p className="text-muted-foreground">Generating comprehensive case overview...</p>
                <p className="text-sm text-muted-foreground mt-2">⏱️ Estimated time: ~45 seconds</p>
                <p className="text-xs text-muted-foreground mt-1">Synthesizing {documents.filter(d => d.processing_status === 'completed').length} document summaries with AI</p>
              </div>
            ) : caseOverview ? (
              <div>
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <div className="whitespace-pre-wrap">{caseOverview}</div>
                </div>
                <div className="mt-6 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={async () => {
                      setCaseOverview(null);
                      setLoadingOverview(true);
                      try {
                        const result = await processingApi.getCaseOverview(Number(projectId), selectedModel);
                        setCaseOverview(result.overview);
                        toast.success('Case overview regenerated');
                      } catch (error) {
                        toast.error(handleApiError(error));
                      } finally {
                        setLoadingOverview(false);
                      }
                    }}
                  >
                    🔄 Regenerate Overview
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">No overview available yet. Process documents first.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Simple Timeline Dialog */}
      <Dialog open={showSimpleTimeline} onOpenChange={setShowSimpleTimeline}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>📅 Document Timeline</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {documents
              .filter(doc => {
                if (doc.processing_status !== 'completed' || !doc.annotation_data) return false;
                // Show if has filed_date OR key_dates
                return doc.annotation_data.filed_date || (doc.annotation_data.key_dates && doc.annotation_data.key_dates.length > 0);
              })
              .flatMap(doc => {
                const events = [];
                // Add filed_date as an event if present
                if (doc.annotation_data?.filed_date) {
                  events.push({
                    doc,
                    date: doc.annotation_data.filed_date,
                    event: `Filed by ${doc.annotation_data.filed_by || 'Unknown'}`,
                    type: 'filing'
                  });
                }
                // Add all key_dates as events
                if (doc.annotation_data?.key_dates && Array.isArray(doc.annotation_data.key_dates)) {
                  doc.annotation_data.key_dates.forEach((kd: any) => {
                    if (kd.date) {
                      events.push({
                        doc,
                        date: kd.date,
                        event: kd.event || 'Event',
                        type: 'key_date'
                      });
                    }
                  });
                }
                return events;
              })
              .sort((a, b) => b.date.localeCompare(a.date)) // Reverse chronological
              .map((item, idx) => (
                <div key={`${item.doc.id}-${idx}`} className="border-l-4 border-blue-500 pl-4 py-2">
                  <div className="flex items-start justify-between mb-1">
                    <span className="font-semibold text-blue-600">
                      {item.date}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {item.doc.annotation_data?.doc_type}
                    </span>
                  </div>
                  <p className="text-sm font-medium mb-1">{item.event}</p>
                  <h3 className="text-xs text-muted-foreground mb-1">{item.doc.filename}</h3>
                  <p className="text-sm text-gray-700 line-clamp-2">{item.doc.annotation_data?.summary || 'No summary available'}</p>
                </div>
              ))}
            {documents.filter(doc => {
              if (doc.processing_status !== 'completed' || !doc.annotation_data) return false;
              return doc.annotation_data.filed_date || (doc.annotation_data.key_dates && doc.annotation_data.key_dates.length > 0);
            }).length === 0 && (
              <p className="text-center text-muted-foreground">No documents with dates yet.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
          </div>
        </div>

        {/* Chat Sidebar - Resizable */}
        <div 
          className="h-screen fixed right-0 top-0 flex z-40"
          style={{ width: `${chatWidth}px` }}
        >
          {/* Resize Handle */}
          <div
            className="w-1 hover:w-2 bg-border hover:bg-primary cursor-col-resize transition-all z-50 relative"
            onMouseDown={(e) => {
              e.preventDefault();
              setIsResizing(true);
            }}
          />
          <div className="flex-1 overflow-hidden">
            <ChatSidebar
              projectId={Number(projectId)}
              selectedModel={selectedModel}
              onClose={() => {}} 
            />
          </div>
        </div>
    </div>
  );
}
